<template>
  <ContentWrap>
    <el-form :inline="true" :model="query">
      <el-form-item label="订单类型">
        <el-select v-model="query.bizType" clearable class="!w-180px" placeholder="全部">
          <el-option label="圈主资格费" value="CIRCLE_OWNER" />
          <el-option label="入圈费" value="CIRCLE_JOIN" />
          <el-option label="六度解锁" value="UNLOCK" />
        </el-select>
      </el-form-item>
      <el-form-item label="用户ID">
        <el-input v-model="query.userId" clearable class="!w-140px" />
      </el-form-item>
      <el-form-item label="圈子ID">
        <el-input v-model="query.circleId" clearable class="!w-140px" placeholder="仅入圈费" />
      </el-form-item>
      <el-form-item label="目标用户">
        <el-input v-model="query.targetUserId" clearable class="!w-140px" placeholder="仅六度" />
      </el-form-item>
      <el-form-item label="支付状态">
        <el-select v-model="query.payStatus" clearable class="!w-140px" placeholder="全部">
          <el-option label="已支付" :value="true" />
          <el-option label="未支付" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">搜索</el-button>
        <el-button @click="reset">重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      show-icon
      title="展示用户购买的圈主资格费（type23）、入圈费（type24）、六度解锁（type22）。资格费/六度价在「参数设置」配；入圈价由圈主发布时设定。"
    />
    <el-table v-loading="loading" :data="list">
      <el-table-column prop="tradeOrderId" label="统一订单ID" width="120" />
      <el-table-column prop="tradeOrderNo" label="订单号" min-width="180" show-overflow-tooltip />
      <el-table-column label="类型" width="120">
        <template #default="s">
          <el-tag v-if="s.row.bizType === 'CIRCLE_OWNER'" type="warning">资格费</el-tag>
          <el-tag v-else-if="s.row.bizType === 'CIRCLE_JOIN'" type="success">入圈费</el-tag>
          <el-tag v-else-if="s.row.bizType === 'UNLOCK'" type="primary">六度解锁</el-tag>
          <span v-else>{{ s.row.bizType }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
      <el-table-column prop="userId" label="用户" width="100" />
      <el-table-column prop="amount" label="金额(元)" width="100" />
      <el-table-column label="支付" width="90">
        <template #default="s">
          <el-tag :type="s.row.payStatus ? 'success' : 'info'">{{ s.row.payStatus ? '已支付' : '未支付' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="交易状态" width="110">
        <template #default="s">{{ tradeStatusText(s.row.tradeStatus) }}</template>
      </el-table-column>
      <el-table-column prop="circleName" label="圈子" min-width="120" show-overflow-tooltip />
      <el-table-column label="目标用户" width="100">
        <template #default="s">{{ s.row.targetUserId || '-' }}</template>
      </el-table-column>
      <el-table-column label="度数" width="80">
        <template #default="s">{{ s.row.pathLength != null ? s.row.pathLength + '度' : '-' }}</template>
      </el-table-column>
      <el-table-column label="业务态" width="110">
        <template #default="s">
          <span v-if="s.row.bizType === 'CIRCLE_OWNER'">{{ ownerStatusText(s.row.ownerStatus) }}</span>
          <span v-else-if="s.row.bizType === 'CIRCLE_JOIN'">{{ joinStatusText(s.row.joinPayStatus) }}</span>
          <span v-else-if="s.row.bizType === 'UNLOCK'">{{ joinStatusText(s.row.unlockPayStatus) }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="payOrderId" label="支付单" width="110" />
      <el-table-column prop="paidTime" label="支付时间" width="170" :formatter="dateFormatter" />
      <el-table-column prop="createTime" label="创建时间" width="170" :formatter="dateFormatter" />
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>
</template>

<script setup lang="ts">
import { dateFormatter } from '@/utils/formatTime'
import * as Api from '@/api/business/circleOrder'

defineOptions({ name: 'BossCircleCircleOrder' })

const loading = ref(false)
const list = ref<Api.CircleOrderVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  bizType: undefined as string | undefined,
  userId: undefined as string | undefined,
  circleId: undefined as string | undefined,
  targetUserId: undefined as string | undefined,
  payStatus: undefined as boolean | undefined
})

const tradeStatusText = (s?: number) =>
  ({ 0: '待支付', 10: '待发货', 20: '已发货', 30: '已完成', 40: '已取消' } as Record<number, string>)[s ?? -1]
  || (s == null ? '-' : String(s))

const ownerStatusText = (s?: number) =>
  ({ 0: '待支付', 1: '待审', 2: '已通过', 3: '已驳回', 4: '已撤销' } as Record<number, string>)[s ?? -1] || '-'

const joinStatusText = (s?: number) =>
  ({ 0: '待支付', 1: '已支付', 2: '已取消' } as Record<number, string>)[s ?? -1] || '-'

const load = async () => {
  loading.value = true
  try {
    const params: any = {
      pageNo: query.pageNo,
      pageSize: query.pageSize,
      bizType: query.bizType || undefined,
      payStatus: query.payStatus
    }
    if (query.userId) params.userId = Number(query.userId)
    if (query.circleId) params.circleId = Number(query.circleId)
    if (query.targetUserId) params.targetUserId = Number(query.targetUserId)
    const data = await Api.getCircleOrderPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const search = () => {
  query.pageNo = 1
  load()
}

const reset = () => {
  query.bizType = undefined
  query.userId = undefined
  query.circleId = undefined
  query.targetUserId = undefined
  query.payStatus = undefined
  search()
}

onMounted(load)
</script>
